import { appState, deviceAliases } from '../core/state';
import { can } from '../core/access';
import { esc } from '../ui/modal';
import { navigate, parseRouteFromHash } from '../core/router';
import { deviceInFarm } from '../farms/farms-view';
import { isDuoFarm } from '../core/farm-kind';
import { renderDeviceLaunchPanel } from './clicker-launch-panel';
import { renderFarmClickerConfig } from './farm-clicker-config';
import { deviceSubtitlesHtml } from '../devices/device-card';
import { t } from '../ui/lang';
/**
 * Per-farm Clicker page (`#!/clicker/farm/:id`). Shows, for admins only, the per-farm
 * clicker configuration (the same form as the farm settings "Clicker" tab); below it
 * the per-device launch panels.
 */
export async function renderClickerFarmDevices(farmId) {
    const content = document.getElementById('content');
    const pageTitle = document.getElementById('page-title');
    pageTitle.textContent = t('clicker.title');
    const isAdmin = can('clicker.config');
    // Resolve the farm record by its stable id (config endpoints are keyed by id).
    let farm;
    try {
        const r = await fetch('/api/farms');
        if (r.ok)
            farm = (await r.json()).find((f) => f.id === farmId);
    }
    catch { /* ignore */ }
    const farmName = farm?.name ?? t('common.farm');
    pageTitle.textContent = t('clicker.title_farm', { farm: farmName });
    content.innerHTML = `
        <div class="farm-devs-header">
            <button class="back-btn" id="back-btn">${t('farms.back_to_farms')}</button>
            <h2 class="settings-title">${esc(farmName)}</h2>
        </div>
        ${isAdmin ? '<div id="clicker-config"></div>' : ''}
        <div class="settings-section">
            <h3 class="settings-section__title">${t('common.devices')}</h3>
            <div class="clicker-device-list" id="clicker-device-list"></div>
        </div>
    `;
    document.getElementById('back-btn').addEventListener('click', () => navigate({ page: 'farms', farmId }));
    if (isAdmin && farm) {
        const configPanel = document.getElementById('clicker-config');
        void renderFarmClickerConfig(configPanel, farm.id);
    }
    // Operators get the per-device launch panel too (full device-panel access),
    // so they need the farm id; only the farm-config section above stays admin-only.
    mountedClickerFarm = { name: farmName, id: farm?.id, kind: farm?.farm_kind };
    void renderClickerDeviceList(document.getElementById('clicker-device-list'), farmName, mountedClickerFarm.id, mountedClickerFarm.kind);
}
// Devices stream in over the WebSocket, so on a fresh page load (refresh) the
// clicker farm page renders before appState.allDevices is populated. Track which
// farm page is mounted and re-render its device list when devices arrive.
let mountedClickerFarm = null;
let renderedDeviceSig = '';
function clickerDeviceSig(farmId, farmName) {
    return [...appState.allDevices.values()]
        .filter((d) => deviceInFarm(d, farmId, farmName))
        .map((d) => `${d.udid}:${d.state}`)
        .sort()
        .join('|');
}
/** Called on every WS device update; re-renders the clicker device list when its
 *  device set changed (so a refresh-then-stream no longer leaves it empty). */
export function onClickerDevicesChange() {
    const route = parseRouteFromHash();
    if (route.page !== 'clicker' || route.farmId == null) {
        mountedClickerFarm = null;
        return;
    }
    if (!mountedClickerFarm || mountedClickerFarm.id !== route.farmId)
        return;
    const listEl = document.getElementById('clicker-device-list');
    if (!listEl)
        return;
    const sig = clickerDeviceSig(mountedClickerFarm.id, mountedClickerFarm.name);
    if (sig === renderedDeviceSig)
        return;
    void renderClickerDeviceList(listEl, mountedClickerFarm.name, mountedClickerFarm.id, mountedClickerFarm.kind);
}
/**
 * Render the farm's devices as a list of expandable panels. Each row toggles a
 * panel with the per-device launch params (pin / password / type / app, mirroring
 * the DuoPlus `remark` fields) and Start/Stop buttons. Operators get the full
 * device panel too; only the Logs button and the farm-config section are
 * admin-only. Without a farm id (shouldn't happen) the row is non-expandable.
 */
async function renderClickerDeviceList(listEl, farmName, farmId, kind) {
    renderedDeviceSig = clickerDeviceSig(farmId, farmName);
    const deviceName = (d) => deviceAliases.get(d.udid) || d['ro.product.model'] || d.udid;
    // Duo farms have no relay, so their devices never stream into appState.allDevices —
    // pull the active DuoPlus phones from the same endpoint the duo device grid uses.
    let devices;
    if (isDuoFarm(kind)) {
        devices = [];
        if (farmId != null) {
            try {
                const r = await fetch(`/api/farms/${farmId}/duo-devices`);
                if (r.ok) {
                    const body = await r.json();
                    devices = (body.devices ?? []).map((d) => ({ udid: d.udid, 'ro.product.model': d.name, state: 'device' }));
                }
            }
            catch { /* leave empty */ }
        }
    }
    else {
        devices = [...appState.allDevices.values()].filter((d) => deviceInFarm(d, farmId, farmName));
    }
    devices.sort((a, b) => deviceName(a).localeCompare(deviceName(b)));
    if (devices.length === 0) {
        listEl.innerHTML = `<p class="empty">${t('clicker.no_devices')}</p>`;
        return;
    }
    // Per-device saved params + live run state (admin + operator with farm access).
    let configs = {};
    let statuses = {};
    if (farmId != null) {
        try {
            const [cr, sr] = await Promise.all([
                fetch(`/api/farms/${farmId}/clicker/devices`),
                fetch(`/api/farms/${farmId}/clicker/status`),
            ]);
            if (cr.ok)
                configs = await cr.json();
            if (sr.ok)
                statuses = await sr.json();
        }
        catch { /* fall back to empty */ }
    }
    listEl.innerHTML = devices.map((d) => {
        const online = d.state === 'device';
        const running = statuses[d.udid]?.state === 'running';
        const name = deviceName(d);
        const cfg = configs[d.udid];
        const expandable = farmId != null;
        return `
            <div class="clicker-device${running ? ' clicker-device--running' : ''}" data-udid="${esc(d.udid)}">
                <div class="clicker-device-row${expandable ? ' clicker-device-row--toggle' : ''}">
                    <span class="status-dot ${online ? 'status-dot--green' : 'status-dot--red'}"></span>
                    <span class="clicker-device-row__name-wrap">
                        <span class="clicker-device-row__name" title="${esc(d.udid)}">${esc(name)}</span>
                        ${deviceSubtitlesHtml({ clickerRunning: running, clickerApp: cfg?.app_name, clickerTask: cfg?.task_type })}
                    </span>
                    <span class="clicker-device-row__meta">${running ? t('clicker.state_running') : online ? t('clicker.state_online') : t('clicker.state_offline')}</span>
                    ${expandable ? '<span class="clicker-device-row__chevron">›</span>' : ''}
                </div>
                ${expandable ? '<div class="clicker-device__panel" hidden></div>' : ''}
            </div>
        `;
    }).join('');
    if (farmId == null)
        return;
    listEl.querySelectorAll('.clicker-device').forEach((card) => {
        const udid = card.dataset['udid'];
        const head = card.querySelector('.clicker-device-row--toggle');
        const panel = card.querySelector('.clicker-device__panel');
        let built = false;
        head.addEventListener('click', () => {
            const open = card.classList.toggle('clicker-device--open');
            panel.hidden = !open;
            if (open && !built) {
                built = true;
                renderDeviceLaunchPanel(panel, farmId, udid, configs[udid], statuses[udid]);
            }
        });
    });
}
